import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import httpClient from '../../utils/httpClient';

interface NPSSurveyModalProps {
  open: boolean;
  onClose: () => void;
}

const RatingButton = styled(IconButton)(({ theme }) => ({
  width: 50,
  height: 50,
  margin: theme.spacing(0.5),
  border: `2px solid ${theme.palette.grey[300]}`,
  borderRadius: '50%',
  fontSize: '1.2rem',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    borderColor: theme.palette.primary.main,
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  }
}));

const NPSSurveyModal: React.FC<NPSSurveyModalProps> = ({ open, onClose }) => {
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const ratingEmojis = ['😞', '😐', '🙂', '😊', '😍'];

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= 1000) {
      setFeedback(value);
    }
  };

  const handleSubmit = async () => {
    if (rating === null) {
      return;
    }

    try {
      const payload = {
        evaluation: (rating + 1),
        description: feedback?.trim() ?? ''
      };
      const res = await httpClient.post('/api/v1/portal/notifications/platform-evaluation', payload);

      const raw = res?.data as unknown;
      const ok = (typeof raw === 'boolean' && raw === true) || (raw && typeof raw === 'object' && (raw as any)?.success === true && Number((raw as any)?.status) === 200 && (raw as any)?.data === true);

      if (!ok) {
        let apiErr = '';
        const errors = (raw as any)?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          apiErr = errors[0]?.errorMessage || errors[0]?.message || '';
        }
        setErrorMessage(apiErr || 'Falha ao enviar sua avaliação.');
        setShowError(true);
        return;
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 1500);
    } catch (error) {
      const respData = (error as any)?.response?.data;
      let apiErr = '';
      if (respData) {
        if (Array.isArray(respData.errors) && respData.errors.length > 0) {
          apiErr = respData.errors[0]?.errorMessage || respData.errors[0]?.message || '';
        } else if (typeof respData.message === 'string') {
          apiErr = respData.message;
        }
      }
      setErrorMessage(apiErr || 'Erro ao enviar sua avaliação.');
      setShowError(true);
    }
  };

  const handleClose = () => {
    setRating(null);
    setFeedback('');
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Como você avalia nossa aplicação?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sua opinião é muito importante para nós!
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
              De 1 a 5, o quanto você recomendaria nossa aplicação?
            </Typography>
            
            <Grid container justifyContent="center" spacing={0.5}>
              {ratingEmojis.map((emoji, index) => (
                <Grid   component={'div' as React.ElementType} item key={index}>
                  <RatingButton
                    onClick={() => handleRatingClick(index)}
                    className={rating === index ? 'selected' : ''}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '1.5rem', lineHeight: 1 }}>
                        {emoji}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                        {index + 1}
                      </Typography>
                    </Box>
                  </RatingButton>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Muito insatisfeito
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Muito satisfeito
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Deixe seu comentário (opcional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Compartilhe suas sugestões, dúvidas ou comentários..."
              value={feedback}
              onChange={handleFeedbackChange}
              inputProps={{ maxLength: 1000 }}
              helperText={`${feedback.length}/1000 caracteres`}
              variant="outlined"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={rating === null}
            sx={{ minWidth: 120 }}
          >
            Enviar Avaliação
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Obrigado pelo seu feedback! Sua avaliação foi enviada com sucesso.
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {errorMessage || 'Falha ao enviar sua avaliação.'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NPSSurveyModal;